import { Checklist } from "@/bryntum-lib/models/Checklist";
import { ChecklistItems } from "@/bryntum-lib/models/ChecklistItems";
import { Store } from "@bryntum/schedulerpro";
import { useCallback, useContext } from "react";
import { toast } from "sonner";
import { User } from "@/bryntum-lib/models/User";
import BryntumContext from "@/react/context/bryntumContext";
import { uploadImageToChecklistItem, checklistItemImageDelete } from "@/network/network";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";

export const ALLOWED_IMAGE_TYPES = ["png", "jpeg", "jpg", "svg", "heif"];

export type TUseSwivuChecklistItemsReturn = ReturnType<typeof useSwivuChecklistItems>;

export const useSwivuChecklistItems = () => {
  const context = useContext(BryntumContext);
  const { project, user } = context;
  // @ts-expect-error - project is not typed
  const { checklistItemStore: checklistItemStoreInstance, boardUsersStore: boardUsersStoreInstance, checklistStore: checklistStoreInstance } = project;
  const checklistItemStore = checklistItemStoreInstance as Store;
  const boardUsersStore = boardUsersStoreInstance as Store;

  const getLastItemOrder = useCallback((store: Store) => {
    return (store.last as unknown as ChecklistItems)?.order || 0;
  }, []);

  const getChecklistItems = useCallback(
    (checklistRecordId: number | string) => {
      const items = checklistItemStore.query((item: ChecklistItems) => item.checklistId === checklistRecordId) as unknown as ChecklistItems[];
      // Sort by order property
      return items.sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    [checklistItemStore]
  );

  const canEditChecklistItem = useCallback(
    (checklistItem: ChecklistItems) => {
      const boardUser = boardUsersStore.find((boardUser: User) => boardUser.userId === user.id) as unknown as User | undefined;
      const checklistRecord = checklistStoreInstance.find((checklist: Checklist) => checklist.id === checklistItem.checklistId) as unknown as Checklist | null;
      if (!checklistRecord) return false;
      return user.can(checklistRecord.canEdit) || checklistRecord.assignedBoardUsers.includes(boardUser?.id);
    },
    [user, boardUsersStore]
  );

  const createChecklistItem = useCallback(
    (checklist: Checklist, description: string = "") => {
      if (checklist.isPhantom) return null;
      const order = getLastItemOrder(checklistItemStore) + 1;
      const checklistItemRecord = checklistItemStore.createRecord({
        description,
        checklistId: checklist.id,
        archived: checklist.archived,
        status: 1,
        order
      });

      checklistItemStore.add(checklistItemRecord);
      return checklistItemRecord as unknown as ChecklistItems;
    },
    [checklistItemStore, getLastItemOrder]
  );

  // Update checklist item
  const updateChecklistItem = useCallback(
    (checklistItem: ChecklistItems, updates: Partial<ChecklistItems>) => {
      if (!canEditChecklistItem(checklistItem)) {
        toast("You don't have permission to edit this checklist item");
        return false;
      }

      Object.assign(checklistItem, updates);
      return true;
    },
    [canEditChecklistItem]
  );

  // Delete checklist item
  const deleteChecklistItem = useCallback(
    (checklistItem: ChecklistItems) => {
      if (!canEditChecklistItem(checklistItem)) {
        toast("You don't have permission to delete this checklist item");
        return false;
      }

      checklistItemStore.remove(checklistItem.id);
      return true;
    },
    [canEditChecklistItem, checklistItemStore]
  );

  // Toggle checklist item check status
  const toggleChecklistItem = useCallback(
    (checklistItem: ChecklistItems) => {
      const currentBoardUser = boardUsersStore.find((boardUser: User) => boardUser.userId === user.id) as unknown as User | undefined;
      const isAssigned = checklistItem.checklistRecord.assignedBoardUsers.includes(currentBoardUser?.id);

      if (!canEditChecklistItem(checklistItem) && !isAssigned) {
        toast("You don't have permission to modify this checklist item");
        return false;
      }

      checklistItem.isItemCheck = !checklistItem.isItemCheck;
      return true;
    },
    [canEditChecklistItem, boardUsersStore]
  );

  // Upload image to checklist item
  const uploadChecklistItemImage = useCallback(
    async (checklistItem: ChecklistItems, file: File) => {
      if (checklistItem.isPhantom) return null;

      const canEdit = canEditChecklistItem(checklistItem);

      if (!canEdit) {
        toast.error("You don't have permission to upload images to this checklist item");
        return false;
      }

      if (!file) {
        toast.error("No file selected");
        return false;
      }

      const fileType = file.type.split("/")[1];
      if (!file.type.includes("image") || !ALLOWED_IMAGE_TYPES.includes(fileType)) {
        toast.error(`Unsupported File Type. Please upload files with extensions: ${ALLOWED_IMAGE_TYPES.join(", ")}`);
        return false;
      }

      try {
        const selectedBoard = getSavedBoardSelection();
        const { data } = await uploadImageToChecklistItem(selectedBoard.id, checklistItem.id as number, file);

        // Update the store with the new image
        checklistItemStore.applyChangeset({
          updated: [
            {
              id: checklistItem.id,
              thumbnail_url: data.thumbnail_url + "?timestamp=" + Date.now(),
              image: data.image_url
            }
          ]
        });

        toast.success("Image uploaded successfully");
        return true;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to upload image. Please try again.";
        toast.error(message);
        return false;
      }
    },
    [checklistItemStore, canEditChecklistItem]
  );

  // Remove image from checklist item
  const removeChecklistItemImage = useCallback(
    async (checklistItem: ChecklistItems) => {
      if (checklistItem.isPhantom) return null;

      const canEdit = canEditChecklistItem(checklistItem);
      const currentBoardUser = boardUsersStore.find((boardUser: User) => boardUser.userId === user.id) as unknown as User | undefined;
      const isAssigned = checklistItem.checklistRecord.assignedBoardUsers.includes(currentBoardUser?.id);

      if (!canEdit && !isAssigned) {
        toast.error("You don't have permission to remove this image");
        return false;
      }

      try {
        await checklistItemImageDelete(checklistItem.id as number);

        // Update the store to remove the image
        checklistItemStore.applyChangeset({
          updated: [{ id: checklistItem.id, thumbnailUrl: null, image: null }]
        });

        toast.success("Image removed successfully");
        return true;
      } catch (error) {
        toast.error("Failed to remove image. Please try again.");
        return false;
      }
    },
    [canEditChecklistItem, boardUsersStore, checklistItemStore]
  );

  // Reorder checklist items
  // This function handles the reordering of checklist items when they are dragged and dropped
  // It receives the reordered array from the Sortable component and updates the order property
  // of all items to maintain proper sorting based on their new positions
  const reorderChecklistItems = useCallback(
    (reorderedItems: ChecklistItems[]) => {
      // Check if there are items to reorder
      if (!reorderedItems || reorderedItems.length === 0) {
        return false;
      }

      // Check if user can edit the checklist
      const firstItem = reorderedItems[0];
      if (firstItem && !canEditChecklistItem(firstItem)) {
        toast.error("You don't have permission to reorder items in this checklist");
        return false;
      }

      // Update the order property for all items based on their position in the reordered array
      reorderedItems.forEach((item, index) => {
        if (item.order !== index + 1) {
          item.order = index + 1;
        }
      });

      return true;
    },
    [canEditChecklistItem]
  );

  return {
    canEditChecklistItem,
    getChecklistItems,
    createChecklistItem,
    updateChecklistItem,
    deleteChecklistItem,
    toggleChecklistItem,
    uploadChecklistItemImage,
    removeChecklistItemImage,
    reorderChecklistItems
  };
};
